import { UserStore } from '../../../auth/model/UserStore';
import { ActivationRepository } from './ActivationRepository';
import { eventsService, AppEvents } from '@Engage.Core';
import { Auth0ClientInstance } from '../../../auth/Auth0ClientInstance';
import { localStorageService } from '@Engage.Data/services/storage/localStorage/LocalStorageService';
import { CookiesStorageService } from '@Engage.Data/services';
import { CookiesKeys } from '@Engage.Data/constants/Constants';
/**
 * Mirrors Connext v2 Activation + ActivateForm + LinkAccountService.
 *
 * Multi-step activation wizard:
 * 1. If user is Subscribed → already activated, reload
 * 2. If user not logged in → show AuthenticateActivation substep
 * 3. If user logged in → show ActivateForm substep
 * 4. On submit → POST link account → show SuccessActivation or ErrorActivation
 */
export class ActivationEngine {
    /**
     * Returns true if Auth0 is the auth system (i.e., Auth0ClientInstance was initialized).
     * When true, login uses redirect flow instead of inline form.
     */
    static isAuth0Active() {
        try {
            return Auth0ClientInstance && Auth0ClientInstance.isReady();
        }
        catch (_a) {
            return false;
        }
    }
    /**
     * Check if activation was pending before an Auth0 redirect.
     * If so, resume the activation flow at the Activate step.
     */
    static checkPendingActivation() {
        try {
            const pending = localStorageService.get(this.ACTIVATION_PENDING_KEY, { expandKey: false });
            if (pending && UserStore.isLoggedIn()) {
                localStorageService.set(this.ACTIVATION_PENDING_KEY, '', { expandKey: false });
                // User came back from Auth0 login — resume activation
                this.process();
            }
        }
        catch ( /* ignore */_a) { /* ignore */ }
    }
    /**
     * Store the ActivationForm data from the paywall response.
     * Called by EngageFlow after processView() returns.
     * Handles both PascalCase (ActivationForm.Html) and camelCase (activationForm.html) from API.
     */
    static setActivationForm(data) {
        var _a, _b, _c, _d, _e, _f;
        if (!data)
            return;
        this.activationFormData = {
            Id: (_b = (_a = data.Id) !== null && _a !== void 0 ? _a : data.id) !== null && _b !== void 0 ? _b : 0,
            Name: (_d = (_c = data.Name) !== null && _c !== void 0 ? _c : data.name) !== null && _d !== void 0 ? _d : '',
            Html: (_f = (_e = data.Html) !== null && _e !== void 0 ? _e : data.html) !== null && _f !== void 0 ? _f : ''
        };
    }
    /**
     * Entry point — called when data-mg2-action="activation" is clicked.
     * Finds the activation template in the DOM and orchestrates substeps.
     */
    static process() {
        var _a, _b, _c, _d;
        if (this.isProcessing)
            return;
        this.isProcessing = true;
        const user = UserStore.getUser();
        // Already subscribed — no activation needed
        if ((user === null || user === void 0 ? void 0 : user.UserState) === 'Subscribed') {
            window.location.reload();
            return;
        }
        eventsService.dispatch(AppEvents.ActivationFormShown, null);
        // Auth0 uses redirect-based login — check BEFORE injecting template
        if (!UserStore.isLoggedIn() && this.isAuth0Active()) {
            localStorageService.set(this.ACTIVATION_PENDING_KEY, 'true', { expandKey: false });
            this.isProcessing = false;
            Auth0ClientInstance.login();
            return;
        }
        // Inject activation template HTML into the DOM if not already present
        this.ensureActivationTemplate();
        // The activation template uses "Authenticate" and "Activate" as step names
        const templateContainer = (_c = (_a = document.querySelector('.connext-activate')) !== null && _a !== void 0 ? _a : (_b = document.querySelector("[data-nxt-template-step='Activation']")) === null || _b === void 0 ? void 0 : _b.closest('.Mg2-connext')) !== null && _c !== void 0 ? _c : (_d = document.querySelector("[data-nxt-template-step='Authenticate']")) === null || _d === void 0 ? void 0 : _d.closest('.Mg2-connext');
        if (!templateContainer) {
            this.isProcessing = false;
            return;
        }
        const authenticateStep = templateContainer.querySelector("[data-nxt-template-step='Authenticate']");
        const activateStep = templateContainer.querySelector("[data-nxt-template-step='Activate']");
        if (!UserStore.isLoggedIn()) {
            // Auth0 uses redirect-based login — skip inline form, redirect to Auth0
            if (this.isAuth0Active()) {
                // Flag that activation is pending so we can resume after redirect
                localStorageService.set(this.ACTIVATION_PENDING_KEY, 'true', { expandKey: false });
                this.isProcessing = false;
                Auth0ClientInstance.login();
                return;
            }
            // MG2 inline form — show authenticate step
            if (authenticateStep)
                authenticateStep.style.display = 'block';
            if (activateStep)
                activateStep.style.display = 'none';
            this.showSubstep('Login');
            eventsService.dispatch(AppEvents.ActivationLoginStepShown, null);
            this.listenForLoginSuccess();
        }
        else if (this.hasExpiredSubscriptions(user)) {
            // Show expired subscription substep if it exists
            if (authenticateStep)
                authenticateStep.style.display = 'none';
            if (activateStep)
                activateStep.style.display = 'block';
            const shown = this.showSubstep('ExpiredSubscription');
            if (!shown) {
                this.showActivateForm();
            }
            else {
                eventsService.dispatch(AppEvents.ActivationExpiredSubscriptionStepShown, null);
            }
        }
        else {
            this.showActivateForm();
        }
    }
    /**
     * Shows the ActivateForm substep and binds the submit button.
     */
    static showActivateForm() {
        // Switch to Activate step, hide Authenticate step
        const container = this.getTemplateContainer();
        if (container) {
            const authenticateStep = container.querySelector("[data-nxt-template-step='Authenticate']");
            const activateStep = container.querySelector("[data-nxt-template-step='Activate']");
            if (authenticateStep)
                authenticateStep.style.display = 'none';
            if (activateStep)
                activateStep.style.display = 'block';
            // Clear any previous errors when re-showing the form
            this.clearFieldErrors(container);
            this.hideError();
        }
        this.showSubstep('ActivateForm');
        // Show/hide SubscribeLink and UpgradeLink AFTER showSubstep
        // (showSubstep hides all substeps, so we must show these after)
        if (container) {
            this.processSubscribeUpgradeLinks(container);
        }
        eventsService.dispatch(AppEvents.ActivationLinkStepShown, null);
        this.bindSearchOptionDropdown();
        this.bindActivateButton();
    }
    /**
     * Shows SubscribeLink or UpgradeLink based on user state.
     * Mirrors ConneXt v2 commonSubSteps processing:
     *   SubscribeLink.isShow() = !User.isSubscribedNotEntitled()
     *   UpgradeLink.isShow() = User.isSubscribedNotEntitled()
     */
    static processSubscribeUpgradeLinks(container) {
        const user = UserStore.getUser();
        const isSubscribedNotEntitled = (user === null || user === void 0 ? void 0 : user.UserState) === 'SubscribedNotEntitled';
        const subscribeLink = container.querySelector("[data-nxt-template-substep='SubscribeLink']");
        const upgradeLink = container.querySelector("[data-nxt-template-substep='UpgradeLink']");
        if (subscribeLink) {
            subscribeLink.style.display = isSubscribedNotEntitled ? 'none' : 'block';
        }
        if (upgradeLink) {
            upgradeLink.style.display = isSubscribedNotEntitled ? 'block' : 'none';
        }
    }
    /**
     * Binds the SearchOption dropdown change event.
     * Mirrors ConneXt v2 ActivateForm.processForm() — on change:
     *   hides previous option substep, shows the selected one
     *   (e.g. ActivateByAccountNumber, ActivateByZipCodeAndHouseNumber, etc.)
     */
    static bindSearchOptionDropdown() {
        const container = this.getTemplateContainer();
        if (!container)
            return;
        const select = container.querySelector("[data-nxt-input='SearchOption']");
        if (!select)
            return;
        let activePartForm = null;
        select.addEventListener('change', () => {
            const value = select.value;
            // Hide previously shown option substep
            if (activePartForm) {
                activePartForm.style.display = 'none';
            }
            // Show the substep matching the selected value
            if (value) {
                activePartForm = container.querySelector(`[data-nxt-template-substep='${value}']`);
                if (activePartForm) {
                    activePartForm.style.display = '';
                }
            }
        });
    }
    /**
     * Binds the activate button click handler.
     * Mirrors Connext v2 ActivateForm.processForm()
     */
    static bindActivateButton() {
        var _a;
        const container = this.getTemplateContainer();
        if (!container)
            return;
        const button = container.querySelector("[data-nxt-button='ActivateAccount'], [data-nxt-button='activate']");
        if (!button)
            return;
        // Remove previous listeners by cloning — prevents duplicate binds on "Try again"
        const newButton = button.cloneNode(true);
        (_a = button.parentNode) === null || _a === void 0 ? void 0 : _a.replaceChild(newButton, button);
        newButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.hideError();
            this.clearFieldErrors(container);
            const formData = this.getFormData(container);
            // Validate required fields based on selected SearchOption
            if (!this.validateActivationForm(container, formData)) {
                return;
            }
            // Show loader on button
            this.setButtonLoading(newButton, true);
            this.submitActivation(formData)
                .finally(() => this.setButtonLoading(newButton, false));
        });
    }
    /**
     * Validates the activation form fields.
     * Mirrors ConneXt v2 Form.validate() — checks required fields and shows inline errors.
     */
    static validateActivationForm(container, formData) {
        let isValid = true;
        // LastName is always required
        if (!formData.LastName) {
            this.showFieldError(container, 'LastName', 'Last name is required');
            isValid = false;
        }
        // SearchOption is always required
        if (!formData.SearchOption) {
            this.showFieldError(container, 'SearchOption', 'Please select a search option');
            isValid = false;
        }
        // Validate fields based on selected search option
        const searchOption = formData.SearchOption;
        if (searchOption === 'ActivateByAccountNumber') {
            if (!formData.AccountNumber) {
                this.showFieldError(container, 'AccountNumber', 'Account number is required');
                isValid = false;
            }
        }
        else if (searchOption === 'ActivateByZipCodeAndHouseNumber') {
            if (!formData.ZipCode) {
                this.showFieldError(container, 'ZipCode', 'Zip code is required');
                isValid = false;
            }
            if (!formData.HouseNumber) {
                this.showFieldError(container, 'HouseNumber', 'House number is required');
                isValid = false;
            }
        }
        else if (searchOption === 'ActivateByZipCodeAndPhoneNumber') {
            if (!formData.ZipCode) {
                this.showFieldError(container, 'ZipCode', 'Zip code is required');
                isValid = false;
            }
            if (!formData.PhoneNumber) {
                this.showFieldError(container, 'PhoneNumber', 'Phone number is required');
                isValid = false;
            }
        }
        else if (searchOption === 'ActivateByConfirmationNumber') {
            if (!formData.ConfirmationNumber) {
                this.showFieldError(container, 'ConfirmationNumber', 'Confirmation number is required');
                isValid = false;
            }
        }
        return isValid;
    }
    /**
     * Shows an inline error for a specific field.
     * Mirrors ConneXt v2 InputErrorMessage — adds 'has-error' class to the input
     * and creates a <div class="input-error"> after the input.
     */
    static showFieldError(container, fieldName, message) {
        var _a;
        // Find the input element(s) — handle multiple inputs with same name (e.g. PhoneNumber parts)
        const inputs = container.querySelectorAll(`[data-nxt-input='${fieldName}']`);
        if (!inputs.length)
            return;
        const input = inputs[inputs.length - 1]; // use last input for positioning (phone number case)
        // Add 'has-error' class to all matching inputs
        inputs.forEach(el => el.classList.add('has-error'));
        // Remove any previous error div for this field
        const prev = container.querySelector(`.input-error[data-field-error='${fieldName}']`);
        if (prev)
            prev.remove();
        // Create a dynamic error div (mirrors ConneXt v2 InputErrorMessage)
        const errorDiv = document.createElement('div');
        errorDiv.className = 'input-error';
        errorDiv.setAttribute('data-field-error', fieldName);
        const p = document.createElement('p');
        p.textContent = message;
        errorDiv.appendChild(p);
        // Adjust margin on input — mirrors ConneXt v2 InputErrorMessage.checkMarginBottom()
        const marginBottom = getComputedStyle(input).marginBottom;
        if (parseInt(marginBottom) > 5) {
            input.style.marginBottom = '5px';
        }
        // Append after the input in its parent — mirrors ConneXt v2 InputErrorMessage.show()
        (_a = input.parentNode) === null || _a === void 0 ? void 0 : _a.appendChild(errorDiv);
    }
    /**
     * Clears all inline field errors.
     * Mirrors ConneXt v2 Field.resetErrors() — removes 'has-error' class,
     * removes dynamic input-error divs, and hides built-in error elements.
     */
    static clearFieldErrors(container) {
        // Remove has-error class from all inputs
        container.querySelectorAll('.has-error').forEach(el => {
            el.classList.remove('has-error');
            // Restore margin
            el.style.marginBottom = '';
        });
        // Remove dynamically created error divs (class="input-error")
        container.querySelectorAll('.input-error').forEach(el => {
            el.remove();
        });
        // Hide built-in error message elements
        container.querySelectorAll('[data-nxt-input-error]').forEach(el => {
            el.textContent = '';
            el.style.display = 'none';
        });
    }
    /**
     * Sets the button loading state.
     * Mirrors ConneXt v2 Loader — adds disabled class and appends a loading indicator.
     */
    static setButtonLoading(button, loading) {
        if (loading) {
            button.classList.add('disabled');
            button.style.pointerEvents = 'none';
            button.setAttribute('data-original-text', button.innerHTML);
            button.innerHTML += ' <img src="data:image/gif;base64,R0lGODlhEAALAPQAAP///wAAANra2tDQ0Orq6gYGBgAAAC4uLoKCgmBgYLq6uiIiIkpKSoqKimRkZL6+viYmJgQEBE5OTubm5tjY2PT09Dg4ONzc3PLy8ra2tqCgoMrKyu7u7gAAAAAAAAAAACH/C05FVFNDQVBFMi4wAwEAAAAh+QQJCwAAACwAAAAAEAALAAAFLSAgjmRpnqSgCuLKAq5AEIM4zDVw03ve27ifDgfkEYe04kDIDC5zrtYKRa2WQgAh+QQJCwAAACwAAAAAEAALAAAFJGBhGAVgnqhpHIeRvsDawqns0qeN5+y967teleR0EUkDEWBhQgAh+QQJCwAAACwAAAAAEAALAAAFNSAgjmRpnqUgCuLKAq5AEIM4zDVw03ve27ifDgfkEYe04kDIDC5zrtYKRa2WQQBIB0EkQgAh+QQJCwAAACwAAAAAEAALAAAFNSAgjmRpnqUgCuLKAq5AEIM4zDVw03ve27ifDgfkEYe04kDIDC5zrtYKRa2WQQBIB0EkQgAh+QQJCwAAACwAAAAAEAALAAAFLSAgjmRpnqSgCuLKAq5AEIM4zDVw03ve27ifDgfkEYe04kDIDC5zrtYKRa2WQgA7" style="margin-left:7px;width:16px;height:11px" />';
        }
        else {
            button.classList.remove('disabled');
            button.style.pointerEvents = '';
            const originalText = button.getAttribute('data-original-text');
            if (originalText) {
                button.innerHTML = originalText;
                button.removeAttribute('data-original-text');
            }
        }
    }
    /**
     * Submits the activation form.
     * Mirrors Connext v2 LinkAccountService.linkAccount() + checkDigitalAccess()
     */
    static async submitActivation(formData) {
        var _a, _b, _c, _d, _e;
        const user = UserStore.getUser();
        // CustomRegId = the user identifier used by the API.
        // In ConneXt v2: User.getCustomerRegistrationId() returns CustomerRegistrationId || IgmRegID
        // For Auth0: this is the auth0 sub claim (e.g. "auth0|679cab...")
        // which is stored as CustomerRegistrationId after syncUser, or in the auth0_sub cookie.
        const customRegId = (_e = (_d = (_c = (_b = (_a = user === null || user === void 0 ? void 0 : user.CustomerRegistrationId) !== null && _a !== void 0 ? _a : user === null || user === void 0 ? void 0 : user.MasterId) !== null && _b !== void 0 ? _b : user === null || user === void 0 ? void 0 : user.UserId) !== null && _c !== void 0 ? _c : user === null || user === void 0 ? void 0 : user.IgmRegID) !== null && _d !== void 0 ? _d : this.getAuth0Sub()) !== null && _e !== void 0 ? _e : null;
        const mode = this.getUserMode(user);
        const payload = {
            ...formData,
            CustomRegId: customRegId,
            Mode: mode
        };
        const searchOption = payload.SearchOption || 'linkAccount';
        eventsService.dispatch(AppEvents.ActivationLinkStepSubmitted, searchOption);
        try {
            const response = await ActivationRepository.linkAccount(searchOption, payload);
            // Check response for errors
            if ((response === null || response === void 0 ? void 0 : response.errorCode) || (response === null || response === void 0 ? void 0 : response.Success) === false) {
                throw { ErrorMessage: (response === null || response === void 0 ? void 0 : response.Message) || 'Activation failed.' };
            }
            // Check digital access after linking — mirrors Connext v2 DigitalAccess.check()
            if (customRegId) {
                const accessResponse = await ActivationRepository.checkDigitalAccess(customRegId, mode);
                const accessError = this.checkDigitalAccess(accessResponse);
                if (accessError) {
                    throw { ErrorMessage: accessError };
                }
            }
            this.successLinking();
        }
        catch (error) {
            const message = (error === null || error === void 0 ? void 0 : error.ErrorMessage) || (error === null || error === void 0 ? void 0 : error.message) || 'Activation failed. Please try again.';
            this.errorLinking(message);
        }
    }
    /**
     * Evaluates the DigitalAccess API response.
     * Mirrors Connext v2 DigitalAccess.check() + AccessLevelModel.
     *
     * Returns null if access is granted (PREMIUM), or an error message string.
     */
    static checkDigitalAccess(response) {
        var _a;
        if (!response) {
            return "I'm sorry, an error occurred and we can't complete this process. Please contact customer service for assistance.";
        }
        // Handle error responses
        if (typeof response === 'string') {
            try {
                const parsed = JSON.parse(response);
                if ((_a = parsed === null || parsed === void 0 ? void 0 : parsed.Errors) === null || _a === void 0 ? void 0 : _a.some((e) => e.Code === 400)) {
                    return "I'm sorry, an error occurred and we can't complete this process. Please contact customer service for assistance.";
                }
            }
            catch (_b) {
                return "I'm sorry, an error occurred and we can't complete this process. Please contact customer service for assistance.";
            }
        }
        // Evaluate AccessLevel
        const level = response.AccessLevel;
        if (!level)
            return null; // no access level info — treat as success
        const accessStr = typeof level === 'string' ? level.toUpperCase() : '';
        if (accessStr === 'PREMIUM' || level.IsPremium) {
            return null; // full access granted
        }
        if (accessStr === 'UPGRADE' || level.IsUpgrade) {
            return "I'm sorry, your subscription does not give you access to this content. Please upgrade to get access.";
        }
        if (accessStr === 'PURCHASE' || level.IsPurchase) {
            return "I'm sorry, your subscription does not give you access to this content. Please subscribe to get access.";
        }
        return null; // unknown level — don't block
    }
    /**
     * On successful link account.
     * Mirrors Connext v2 ActivateForm.successLinking()
     */
    static successLinking() {
        this.showSubstep('Success');
        eventsService.dispatch(AppEvents.ActivationLinkSuccessStepShown, null);
        eventsService.dispatch(AppEvents.ActivationAccountCreated, null);
        eventsService.dispatch(AppEvents.ActivationFormClosed, {
            CloseEvent: 'moveToSuccessStep',
            ActivateStatus: 'success'
        });
        // Reload after delay to refresh user state — mirrors Connext v2 isNeedRerunConnext
        setTimeout(() => window.location.reload(), 2000);
    }
    /**
     * On failed link account.
     * Mirrors Connext v2 ActivateForm.errorLinking() + FailActivation
     */
    static errorLinking(message) {
        const container = this.getTemplateContainer();
        const errorSubstepShown = this.showSubstep('Fail');
        if (errorSubstepShown && container) {
            // Inject error message into the substep — mirrors ConneXt v2 FailActivation constructor
            const failStep = container.querySelector("[data-nxt-template-substep='Fail']");
            if (failStep) {
                // Try the exact ConneXt v2 pattern: [data-nxt-role="linkingErrorMessage"] span
                const msgSpan = failStep.querySelector('[data-nxt-role="linkingErrorMessage"] span');
                if (msgSpan) {
                    msgSpan.innerHTML = message;
                }
                else {
                    // Fallback to other error containers
                    const msgEl = failStep.querySelector('.connext-actflow-fail-message-text, [data-nxt-form-error], .connext-error-message');
                    if (msgEl) {
                        msgEl.innerHTML = message;
                        msgEl.style.display = '';
                    }
                }
                // Bind "Try again" (backStep) link — mirrors ConneXt v2 FailActivation.process()
                this.bindBackStep(failStep);
            }
        }
        else {
            // No Fail substep — show inline error in the form
            this.showError(message);
        }
        eventsService.dispatch(AppEvents.ActivationLinkErrorStepShown, null);
        this.isProcessing = false;
    }
    /**
     * Binds the "Try again" (backStep) action in the Fail substep.
     * Mirrors ConneXt v2 FailActivation.process() — hides Fail, re-shows ActivateForm.
     */
    static bindBackStep(failStep) {
        var _a;
        if (!failStep)
            return;
        // ConneXt v2 uses data-mg2-action="backStep" (note: some templates have typo "data-mg2-acton")
        const tryAgainLink = failStep.querySelector("[data-mg2-action='backStep'], [data-mg2-acton='backStep']");
        if (!tryAgainLink)
            return;
        // Remove any previous listeners by cloning
        const newLink = tryAgainLink.cloneNode(true);
        (_a = tryAgainLink.parentNode) === null || _a === void 0 ? void 0 : _a.replaceChild(newLink, tryAgainLink);
        newLink.addEventListener('click', (e) => {
            e.preventDefault();
            // Hide Fail substep, re-show ActivateForm — mirrors FailActivation.process()
            failStep.style.display = 'none';
            this.showActivateForm();
            eventsService.dispatch(AppEvents.ActivationLinkErrorStepClosed, { CloseEvent: 'moveToActivate' });
        });
    }
    /**
     * Listen for login success to transition from authenticate to activate step.
     */
    static listenForLoginSuccess() {
        const handler = () => {
            eventsService.dispatch(AppEvents.ActivationLoginStepClosed, null);
            UserStore.warmCache();
            this.showActivateForm();
        };
        document.addEventListener('onLoginSuccess', handler, { once: true });
        // Also listen via eventsService for internal login events
        eventsService.subscribe(AppEvents.LoginSuccess, handler, true);
    }
    // ─── Activation Template Injection ──────────────────────────────────────────
    /**
     * Injects ActivationForm.Html from the paywall response into the DOM as a modal
     * if it hasn't been rendered yet.
     * Mirrors ConneXt v2 TemplateStepsGroup.createTemplate() + showTemplate().
     */
    static ensureActivationTemplate() {
        var _a;
        // Already in DOM — nothing to do
        if (document.querySelector('.connext-activate') ||
            document.querySelector('#ConneXt_Authentication-Activation'))
            return;
        if (!((_a = this.activationFormData) === null || _a === void 0 ? void 0 : _a.Html))
            return;
        const html = this.activationFormData.Html;
        // Create modal container — mirrors ConneXt v2 BsModal + ActivationTemplateStepsGroup
        const wrapper = document.createElement('div');
        wrapper.className = 'engage-template Mg2-connext modal-scrollable';
        wrapper.id = 'ConneXt_Authentication-Activation';
        wrapper.innerHTML = html;
        // Create backdrop
        const backdrop = document.createElement('div');
        backdrop.className = 'connext-modal-backdrop fade';
        backdrop.style.opacity = '0.7';
        document.body.appendChild(backdrop);
        document.body.appendChild(wrapper);
        // Setup close button handlers
        wrapper.querySelectorAll('[data-dismiss="modal"], .closebtn, .fa-times').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                wrapper.remove();
                backdrop.remove();
                document.body.style.overflow = '';
                document.body.classList.remove('page-overflow');
                this.isProcessing = false;
                eventsService.dispatch(AppEvents.ActivationFormClosed, {
                    CloseEvent: 'userClose',
                    ActivateStatus: null
                });
            });
        });
        // Prevent body scroll while activation modal is open
        document.body.classList.add('page-overflow');
        document.body.style.overflow = 'hidden';
    }
    // ─── DOM Helpers ──────────────────────────────────────────────────────────
    /** Gets the activation template container element */
    static getTemplateContainer() {
        var _a, _b, _c;
        return (_c = (_a = document.querySelector('#ConneXt_Authentication-Activation')) !== null && _a !== void 0 ? _a : (_b = document.querySelector('.connext-activate')) === null || _b === void 0 ? void 0 : _b.closest('.Mg2-connext')) !== null && _c !== void 0 ? _c : null;
    }
    static showSubstep(name) {
        const container = this.getTemplateContainer();
        if (!container)
            return false;
        const target = container.querySelector(`[data-nxt-template-substep='${name}']`);
        if (!target)
            return false;
        // Hide all substeps within the same step as the target
        const parentStep = target.closest('[data-nxt-template-step]');
        if (parentStep) {
            parentStep.querySelectorAll(':scope > * [data-nxt-template-substep]')
                .forEach(el => el.style.display = 'none');
        }
        target.style.display = '';
        return true;
    }
    static getFormData(container) {
        const data = {};
        // Only collect inputs from the active Activate step (not Authenticate step)
        const activateStep = container.querySelector("[data-nxt-template-step='Activate']");
        const scope = activateStep || container;
        const inputs = scope.querySelectorAll('[data-nxt-input], input[name], select[name]');
        inputs.forEach(input => {
            var _a, _b;
            const key = input.getAttribute('data-nxt-input') || input.name;
            if (!key)
                return;
            const value = (_b = (_a = input.value) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : '';
            // Skip empty optional fields — only include fields that have values
            // Always include required fields: LastName, SearchOption
            if (value || key === 'LastName' || key === 'SearchOption') {
                // For phone number parts, concatenate them
                if (key === 'PhoneNumber' && input.getAttribute('data-nxt-input-phone-number')) {
                    data[key] = (data[key] || '') + value;
                }
                else {
                    data[key] = value;
                }
            }
        });
        // Remove empty string values to match ConneXt v2 behavior (only send what's needed)
        const cleanData = {};
        for (const key of Object.keys(data)) {
            if (data[key] !== '') {
                cleanData[key] = data[key];
            }
        }
        return cleanData;
    }
    static showError(message) {
        var _a;
        const container = this.getTemplateContainer();
        if (!container)
            return;
        const el = (_a = container.querySelector('[data-nxt-form-error]')) !== null && _a !== void 0 ? _a : container.querySelector('.connext-actflow-form-error');
        if (el) {
            el.textContent = message;
            el.style.display = 'block';
        }
    }
    static hideError() {
        const container = this.getTemplateContainer();
        if (!container)
            return;
        const selectors = ['[data-nxt-form-error]', '.connext-actflow-form-error'];
        selectors.forEach(sel => {
            container.querySelectorAll(sel).forEach(el => {
                el.textContent = '';
                el.style.display = 'none';
            });
        });
    }
    static hasExpiredSubscriptions(user) {
        var _a;
        return ((_a = user === null || user === void 0 ? void 0 : user.Subscriptions) === null || _a === void 0 ? void 0 : _a.length) > 0 && user.UserState !== 'Subscribed';
    }
    /**
     * Mirrors ConneXt v2 User.getUserMode().
     * Returns 0 if CustomerRegistrationId exists, 1 if IgmRegID exists, 0 for MasterId/UserId.
     */
    static getUserMode(user) {
        if (!user)
            return 0;
        if (user.CustomerRegistrationId)
            return 0;
        if (user.IgmRegID)
            return 1;
        if (user.MasterId || user.UserId)
            return 0;
        return 0;
    }
    /**
     * Reads the Auth0 sub claim from the auth0_sub cookie.
     * Used as a fallback for CustomRegId when user data hasn't been synced yet.
     */
    static getAuth0Sub() {
        try {
            const sub = CookiesStorageService.get(CookiesKeys.Auth0AccountId, { expandKey: false, useRootDomain: true });
            return sub || null;
        }
        catch (_a) {
            return null;
        }
    }
    /** Reset processing state (called on close) */
    static reset() {
        this.isProcessing = false;
    }
}
ActivationEngine.isProcessing = false;
/** Cached ActivationForm from the paywall response */
ActivationEngine.activationFormData = null;
/** LocalStorage key to flag that activation should resume after Auth0 redirect */
ActivationEngine.ACTIVATION_PENDING_KEY = 'nxt_activation_pending';
